import {
  Card,
  IconButton,
  Link,
  Stack,
  Typography,
  Button,
  Chip,
  Box,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import axios from "axios";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { AuthorizationStatus, AuthorizationType, FundingAuthorization } from "../types";
import { ArrowForward } from "@mui/icons-material";
import { useState } from "react";
import { DocumentPreviewOverlay } from "./DocumentPreviewOverlay";

interface Props {
  orderNumber: string | undefined;
  fundingAuthorization: FundingAuthorization | undefined;
  refreshData: () => Promise<void>;
  disabled?: boolean;
}
const ReviewUploadFunding = (props: Props) => {
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
  const [openIframe, setOpenIframe] = useState(false);

  const authTypeDisplayName = {
    [AuthorizationType.BuyerBorrower]: "Buyer/Borrower",
    [AuthorizationType.Seller]: "Seller",
    [AuthorizationType.Lender]: "Lender",
    [AuthorizationType.Escrow]: "Escrow Officer"
  };
  const submitApproved = async () => {
    if (props.disabled) return;
    await axios.post("/api/FundingPackages/packages/UpdateFundingAuthorizationStatus", {
      authorizationId: props.fundingAuthorization?.id,
      status: AuthorizationStatus.Approved
    });
    setOpenIframe(false);
    props.refreshData();
  };
  const submitRejected = async () => {
    if (props.disabled) return;
    await axios.post("/api/FundingPackages/packages/UpdateFundingAuthorizationStatus", {
      authorizationId: props.fundingAuthorization?.id,
      status: AuthorizationStatus.Rejected
    });
    setOpenIframe(false);
    await props.refreshData();
  };
  const cleanContactType = (type: string) => {
    if (!type) return "";
    const normalized = type.toLowerCase();

    if (["buyer/borrower", "seller", "lender", "escrow"].includes(normalized)) {
      return "";
    }
    if (normalized.includes("signatory")) return "Signatory";
    if (normalized.includes("attorney")) return "Attorney";
    if (normalized.includes("escrow")) return "Escrow Officer";

    return type;
  };
  const sortedContacts = [...(props.fundingAuthorization?.contacts || [])].sort((a, b) => {
    const mainTypes = ["Buyer/Borrower", "Seller", "Lender"];
    const aIsMain = mainTypes.includes(a.contactType);
    const bIsMain = mainTypes.includes(b.contactType);

    if (aIsMain && !bIsMain) return -1;
    if (!aIsMain && bIsMain) return 1;
    return 0;
  });
  return (
    <>
      <Stack direction="row" spacing={2}>
        <Card
          sx={{
            p: 2,
            width: 410,
            height: 180,
            zIndex: 40
          }}
        >
          <Stack direction={"row"} justifyContent={"space-between"} width={'100%'}>
            <Stack>
              <Typography fontWeight="bold" marginBottom={1}>
                {authTypeDisplayName[props.fundingAuthorization?.type ?? AuthorizationType.BuyerBorrower]} Auth
                {props.fundingAuthorization?.type === AuthorizationType.Lender &&
                props.fundingAuthorization?.contacts?.[0]?.contactTypeNumber != null
                  ? ` ${props.fundingAuthorization.contacts[0].contactTypeNumber}`
                  : ""}
              </Typography>

              {sortedContacts.map((contact) => {
                const typeLabel = cleanContactType(contact.contactType);
                return (
                  <Stack key={contact.id} direction="row" alignItems="center" spacing={0.5}>
                    {contact.contactType !== "Escrow" && (
                    <>
                    <AccountBalanceIcon fontSize="small" color="action" />
                    {typeLabel && (
                      <Typography variant="caption" color="text.secondary" fontSize={11} fontWeight={'bold'}>
                        {typeLabel}
                      </Typography>
                    )}
                    <Stack direction="row" alignItems="center">
                      <Typography variant="body2" color="text.primary" fontSize={13}>
                        {contact.contactName}
                      </Typography>
                    </Stack>
                    </>)}
                  </Stack>
                );
              })}
            </Stack>
            <Stack direction={"column"} ml={4} maxWidth={100}>
              {props.fundingAuthorization?.authorizationStatus === 2 ? (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ cursor: "pointer" }}
                  onClick={() => setOpenIframe(true)}
                >
                  <Chip label="Approved" color="success" size="small" />
                </Stack>
              ) : props.fundingAuthorization?.authorizationStatus === 3 ? (
                <>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ cursor: "pointer" }}
                    onClick={() => setOpenIframe(true)}
                  >
                    <Chip label="Insufficient" color="error" size="small" />
                  </Stack>
                </>
              ) : (
                <>
                  {props.fundingAuthorization?.type === 3 && !props.fundingAuthorization.isAuthorizationRequired ? (
                    <FormControlLabel
                      control={<Checkbox checked={true} />}
                      label="Authorization is not required by Lender."
                    />
                  ) : (
                    <Stack>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ArrowForward />}
                        sx={{
                          backgroundColor: "blue",
                          color: "white",
                          textTransform: "none",
                          width: 80,
                          "&:hover": {
                            backgroundColor: "blue",
                            borderColor: "blue"
                          }
                        }}
                        onClick={() => setOpenIframe(true)}
                        disabled={props.disabled}
                      >
                        Review
                      </Button>
                      {/* <Stack direction="row" spacing={1.5} alignItems="center">
                        <Link
                          target="_blank"
                          underline="hover"
                          href={`${momentumUrl}opendoc?id=${props.fundingAuthorization?.momentumDocumentId}`}
                          fontSize={13}
                        >
                          {props.fundingAuthorization?.momentumDocumentName}
                        </Link>
                      </Stack> */}
                    </Stack>
                  )}
                </>
              )}
              <Link
                target="_blank"
                underline="hover"
                href={`${momentumUrl}opendoc?id=${props.fundingAuthorization?.momentumDocumentId}`}
                fontSize={13}
              >
                {props.fundingAuthorization?.momentumDocumentName}
              </Link>
            </Stack>
          </Stack>
        </Card>
        {openIframe && (
          <DocumentPreviewOverlay
            open={openIframe}
            momentumUrl={momentumUrl}
            documentId={props.fundingAuthorization?.momentumDocumentId}
            onClose={() => setOpenIframe(false)}
            onApprove={submitApproved}
            onReject={submitRejected}
          />
        )}
      </Stack>
    </>
  );
};

export default ReviewUploadFunding;
